import type { TranslationKeys } from '../types';

/**
 * Japanese translations
 * 日本語翻訳
 */
export const jaJP: TranslationKeys = {
  language: {
    cmn: '中国語',
    eng: '英語',
    jpn: '日本語',
    kor: '韓国語',
    spa: 'スペイン語',
    fra: 'フランス語',
    deu: 'ドイツ語',
    rus: 'ロシア語',
    por: 'ポルトガル語',
    ita: 'イタリア語',
    yue: '粵語',
    nld: 'オランダ語',
    arb: 'アラビア語',
    ind: 'インドネシア語',
  },

  // 共通
  common: {
    loading: '読み込み中...',
    error: 'エラー',
    success: '成功',
    cancel: 'キャンセル',
    confirm: '確認',
    save: '保存',
    delete: '削除',
    edit: '編集',
    close: '閉じる',
    back: '戻る',
    next: '次へ',
    previous: '前へ',
    retry: '再試行',
    refresh: '更新',
    saving: '保存中...',
    processing: '処理中...',
    understood: '了解しました',
    popular: '人気の言語',
    tokenNotEnough: 'トークンが不足しています。購入またはアップグレードしてください'
  },

  // ナビゲーション
  nav: {
    home: 'ホーム',
    translation: 'リアルタイム翻訳',
    history: '会話履歴',
    settings: '設定',
    help: 'ヘルプ',
    about: 'アプリについて',
    profile: 'プロフィール',
    logout: 'ログアウト',
  },

  // 翻訳機能
  translation: {
    startTranslation: '翻訳開始',
    stopTranslation: '翻訳停止',
    stopping: '停止中...',
    realTimeTranslation: 'リアルタイム翻訳',
    sourceLanguage: '元言語',
    targetLanguage: '翻訳先言語',
    swapLanguages: '言語を交換',
    selectLanguage: '言語を選択',
    selectLanguageDescription :'翻訳したい2つの言語を選択してください',
    detectLanguage: '自動検出',
    translating: '翻訳中...',
    translationComplete: '翻訳完了',
    translationError: '翻訳失敗',
    noTranslationYet: '翻訳はまだありません',
    speakNow: '話し始めてください...',
    listening: '聞き取り中...',
    processing: '処理中...',
    sameLanguageError: 'ソース言語とターゲット言語は同じにできません。異なる言語を選択してください。',
  },

  // オーディオ/マイク
  audio: {
    microphonePermission: 'マイクの許可',
    microphonePermissionDenied: 'マイクの許可が拒否されました',
    selectMicrophone: 'マイクを選択',
    noMicrophoneFound: 'マイクが見つかりません',
    microphoneError: 'マイクエラー',
    requestPermission: 'マイクの許可をお願いします',
    permissionGranted: '許可が与えられました',
    permissionRequired: '翻訳を開始するにはマイクの許可が必要です',
  },

  // 設定
  settings: {
    title: '設定',
    language: '言語設定',
    uiLanguage: 'インターフェース言語',
    theme: 'テーマ',
    audio: 'オーディオ設定',
    account: 'アカウント設定',
    privacy: 'プライバシー設定',
    about: 'アプリについて',
    version: 'バージョン情報',
    description: 'アプリケーションオプションを設定',
    profile: 'プロフィール',
    security: 'セキュリティ',
    dangerZone: '危険な操作',
    pleaseLogin: 'アカウント設定を表示するにはログインしてください',
  },

  // プロフィール設定
  profile: {
    displayName: '表示名',
    email: 'メールアドレス',
    emailNotifications: 'メール通知',
    emailNotificationsDesc: '重要な更新や通知をメールで受信',
    memberSince: '登録日',
    enterName: '名前を入力してください',
    nameRequired: '名前は必須です',
    updateSuccess: 'プロフィールが正常に更新されました',
    updateFailed: 'プロフィールの更新に失敗しました',
    avatarUploadComingSoon: 'アバターアップロード機能は近日公開予定',
  },

  // セキュリティ設定
  security: {
    twoFactorAuth: '二要素認証',
    mfaDescription: 'アカウントに追加のセキュリティ層を追加',
    enableMfa: '二要素認証を有効にする',
    disable: '無効',
    enable: '有効',
    enabled: '有効',
    mfaEnabled: '二要素認証が有効になりました',
    mfaDisabled: '二要素認証が無効になりました',
    mfaUpdateFailed: '二要素認証の設定に失敗しました',
    socialLoginMfaWarning: 'ソーシャルログインアカウントでは二要素認証は利用できません',
    socialLoginMfaWarningDesc: 'ソーシャルログインアカウントのセキュリティ設定は第三者プロバイダーによって管理されます',
    password: 'パスワード',
    changePassword: 'パスワード変更',
    passwordDescription: 'アカウントのセキュリティを保つため定期的にパスワードを更新してください',
    currentPassword: '現在のパスワード',
    newPassword: '新しいパスワード',
    confirmPassword: '新しいパスワードの確認',
    updatePassword: 'パスワードを更新',
    passwordChanged: 'パスワードが正常に変更されました',
    passwordChangeFailed: 'パスワードの変更に失敗しました',
    passwordMismatch: 'パスワードが一致しません',
    passwordTooShort: 'パスワードは8文字以上である必要があります',
    loginHistory: 'ログイン履歴',
    lastLogin: '最終ログイン',
    never: 'なし',
    accountCreated: 'アカウント作成日',
    loginMethod: 'ログイン方法',
    socialLogin: 'ソーシャルログイン',
    socialLoginConnected: '{provider}アカウントと連携済み',
    socialLoginNote: '注意',
    socialLoginNoteDesc: '{provider}ソーシャルログインを使用しています。パスワードとセキュリティ設定は{provider}によって管理されます',
    mfaNotAvailable: '二要素認証は利用できません',
    mfaNotAvailableTitle: 'ソーシャルログインアカウントでは利用できません',
    mfaNotAvailableDesc: 'ソーシャルログインアカウントのセキュリティ設定は第三者プロバイダーによって管理されており、ここで設定することはできません',
    mfaModalDescription: '二要素認証を有効にしてアカウントのセキュリティを強化',
    mfaWillProvide: '二要素認証により以下が提供されます：',
    mfaBenefit1: '追加のアカウントセキュリティ保護',
    mfaBenefit2: '不正アクセスの防止',
    mfaBenefit3: 'セキュリティベストプラクティスへの準拠',
    mfaWarning: '有効にすると、ログインのたびに認証コードの入力が必要になります',
    logoutAllDevices: 'すべてのデバイスからログアウト',
    logoutAllDevicesDesc: 'ログイン中のすべてのデバイスとセッションから強制ログアウト',
    logout: 'ログアウト',
    delete: '削除',
    deleteAccount: 'アカウント削除',
    deleteAccountDesc: 'アカウントと関連するすべてのデータを永久に削除',
    confirmLogout: 'ログアウトの確認',
    confirmLogoutDesc: 'すべてのデバイスからログアウトされ、再度ログインが必要になります',
    confirmDelete: 'アカウント削除の確認',
    confirmDeleteDesc: 'この操作は元に戻すことができず、アカウントとすべてのデータが永久に削除されます',
    deleteWarning: '警告：この操作は元に戻すことができません',
    deleteWarning1: 'すべての個人データが永久に削除されます',
    deleteWarning2: '会話履歴と設定が失われます',
    deleteWarning3: '削除されたアカウントは復元できません',
    deleteConfirmLabel: '削除を確認するため以下のテキストを入力してください',
    logoutSuccess: 'すべてのデバイスから正常にログアウトしました',
    logoutFailed: 'ログアウトに失敗しました。もう一度お試しください',
    accountDeleted: 'アカウントが正常に削除されました',
    accountDeleteFailed: 'アカウントの削除に失敗しました。もう一度お試しください',
    deleteConfirmError: '正しい確認テキストを入力してください',
  },

  // エラー
  errors: {
    networkError: 'ネットワーク接続エラー',
    serviceUnavailable: 'サービスが一時的に利用できません',
    translationFailed: '翻訳に失敗しました。もう一度お試しください',
    audioError: 'オーディオデバイスエラー',
    permissionError: '許可エラー',
    unknownError: '不明なエラー',
    tryAgain: 'もう一度お試しください',
  },

  // ホームページ
  home: {
    title: 'フラッシュキャット AI 翻訳',
    subtitle: 'スムーズなリアルタイム翻訳',
    description: '複数言語をサポートするリアルタイム音声翻訳でシームレスなコミュニケーション',
    getStarted: '始める',
    features: {
      realTime: 'リアルタイム翻訳',
      multiLanguage: '多言語サポート',
      highQuality: '高品質翻訳',
    },
  },

  // 無料トライアル
  freeTrial: {
    title: 'トライアル中',
    description: 'ログインして全機能を利用',
    tokensRemaining: '残りトークン',
    timeRemaining: '残り時間',
    upgradeNow: '今すぐアップグレード',
    trialExpired: 'トライアル期間終了',
    trialEnded: 'トライアル終了',
    trialUnavailable: '無料トライアル利用不可',
    getOfficialAccount: '正式アカウント取得',
    loginForMore: 'ログインしてもっと利用',
    expired: '期限切れ',
    tokensExhausted: 'トライアルトークン使い切り',
    notEligible: 'トライアル対象外',
    errorMessage: 'エラーメッセージ',
    reason: '理由',
    solution: '解決方法',
    registerLogin: '登録・ログインして正式アカウントを取得し、全機能をお楽しみください',
    immediateRegister: '今すぐ登録',
    conversationsNotSaved: '会話は保存されません',
    experienceOnly: '体験のみ',
    loginForPermanentSave: 'ログインして永続保存',
    hours: '時間',
    minutes: '分',
    unlimited: '無制限',
    freeQuota: '無料枠',
    tokens: 'トークン',
    pro: 'Pro',

    // Banner specific texts
    betaStage: 'ベータ段階',
    betaInProgress: 'ベータテスト中',
    productInBeta: 'プロダクトベータテスト中...',
    joinWaitlist: 'ウェイトリストに参加',
    joinWaitlistFull: 'ウェイトリストに参加',
    welcomeJoinWaitlist: 'ウェイトリストに参加して優先アクセスを取得しましょう！',
    freeTrialActive: '無料トライアル中',
    freeTrialNotAvailable: '無料トライアル利用不可',
    trialEndedShort: 'トライアル終了',
    freeTrialEndedFull: '無料トライアル終了',
    tokensUsedUp: 'トークン使い切り',
    tokensUsedUpDesc: '無料トライアルの割り当てがすべて使用されました',
    trialExpiredDesc: '無料トライアル期間が終了しました',
    registerForMore: '今すぐ登録',
    registerForMoreDesc: 'より多くのトークンを取得し、会話履歴を永続的に保存',
    fullFeatureExperience: '全機能体験',
    fullFeatureExperienceDesc: 'すべての高度な翻訳機能をアンロック',
    remainingTokens: '残りトークン',
    remainingTime: '残り時間',
    freeTrialNotice: '無料トライアル通知',
    conversationsNotSavedDesc: '翻訳ページを離れると会話履歴が失われます',
    experienceOnlyDesc: 'リアルタイム翻訳機能の完全な効果を体験できます',
    loginForPermanentSaveDesc: '登録・ログイン後、すべての会話履歴がアカウントに永続的に保存されます',
    deviceUsedTrialExpired: 'このデバイスは既に無料トライアルを使用しており、期限が切れています',
    deviceUsedTrialExhausted: 'このデバイスは既に無料トライアルを使用しており、トークンが使い切られています',
  },

  // 認証
  auth: {
    // 共通
    login: 'ログイン',
    logout: 'ログアウト',
    register: '登録',
    email: 'メール',
    password: 'パスワード',
    callback: '認証コールバック',
    forgotPassword: 'パスワードを忘れた',
    rememberMe: 'ログイン状態を保持',
    loginSuccess: 'ログイン成功',
    loginError: 'ログイン失敗',
    logoutSuccess: 'ログアウト成功',

    // ログインフォーム
    welcomeBack: 'おかえりなさい',
    loginWithSocialOrEmail: 'ソーシャルアカウントまたはメールでログイン',
    emailPlaceholder: 'm@example.com',
    forgotPasswordLink: 'パスワードをお忘れですか？',
    loggingIn: 'ログイン中...',
    dontHaveAccount: 'アカウントをお持ちでないですか？',
    signUp: '新規登録',

    // 登録フォーム
    createAccount: 'アカウント作成',
    verifyYourEmail: 'メールアドレスを確認',
    signUpWithSocialOrEmail: 'AppleまたはGoogleアカウントで登録',
    enterVerificationCode: 'メールに送信された確認コードを入力',
    fullName: '氏名',
    fullNamePlaceholder: 'お名前',
    passwordPlaceholder: '8文字以上',
    confirmPassword: 'パスワード確認',
    confirmPasswordPlaceholder: 'パスワードを確認',
    invitationCode: '招待コード',
    invitationCodeRequired: '招待コード',
    invitationCodePlaceholder: '4文字の招待コードを入力',
    invitationCodeDescription: '招待メールの大文字小文字を区別する4文字のコード',
    verificationCode: '確認コード',
    verificationCodePlaceholder: '6桁のコード',
    codeSentTo: '{email}にコードを送信しました',
    resendVerificationCode: '確認コードを再送信',
    sendVerificationCode: '確認コードを送信',
    createAccountButton: 'アカウント作成',
    sendingCode: 'コード送信中...',
    creatingAccount: 'アカウント作成中...',
    alreadyHaveAccount: 'すでにアカウントをお持ちですか？',
    signIn: 'ログイン',
    backToPreviousStep: '前のステップに戻る',

    // パスワード忘れフォーム
    resetPassword: 'パスワードリセット',
    setNewPassword: '新しいパスワードを設定',
    enterEmailForReset: 'メールアドレスを入力すると、リセットコードをお送りします',
    newPassword: '新しいパスワード',
    newPasswordPlaceholder: '8文字以上',
    confirmNewPassword: '新しいパスワード確認',
    confirmNewPasswordPlaceholder: '新しいパスワードを確認',
    sendingCodeLoading: 'コード送信中...',
    verifying: '確認中...',
    resettingPassword: 'パスワードリセット中...',
    verifyCode: 'コード確認',
    resetPasswordButton: 'パスワードリセット',
    rememberPassword: 'パスワードを思い出しましたか？',
    backToLogin: 'ログインに戻る',

    // MFA 二要素認証
    verificationCodeMFA: '確認コード',
    sendCode: 'コード送信',
    sending: '送信中...',
    resendCode: 'コード再送信',
    clickSendCodeDescription: '「コード送信」をクリックしてメールで確認コードを受信',

    // ソーシャルログイン
    orContinueWith: 'または続行',
    orContinueWithEmail: 'またはメールで続行',
    apple: 'Apple',
    google: 'Google',

    // エラーメッセージ
    pleaseEnterEmail: 'メールアドレスを入力してください',
    pleaseEnterEmailFirst: '最初にメールアドレスを入力してください',
    pleaseEnterVerificationCode: '確認コードを入力してください',
    passwordsDoNotMatch: 'パスワードが一致しません',
    passwordTooShort: 'パスワードは8文字以上である必要があります',
    invitationCodeRequiredError: 'ベータテスト期間中は招待コードが必要です',
    failedToSendCode: '確認コードの送信に失敗しました',
    invalidVerificationCode: '無効な確認コード',
    passwordResetFailed: 'パスワードリセットに失敗しました',
    registrationFailed: '登録に失敗しました。もう一度お試しください',
    mfaRequired: '二要素認証コードを入力してください',
    userDoesNotExist: 'ユーザーが存在しません',
    invalidCredentials: 'メールアドレスまたはパスワードが無効です',

    // 成功メッセージ
    verificationCodeSent: 'メールに確認コードを送信しました',
    passwordResetCodeSent: 'メールにパスワードリセットコードを送信しました',
    codeVerified: 'コードが確認されました。新しいパスワードを設定してください',
    passwordResetSuccessful: 'パスワードリセット成功！新しいパスワードでログインしてください',
    registrationSuccessful: '登録成功！リダイレクト中...',
    mfaCodeSent: 'MFAコードをメールに送信しました',

    // 法的条項
    termsAndPrivacy: '続行をクリックすることで、以下に同意したものとみなされます',
    termsOfService: '利用規約',
    and: 'および',
    privacyPolicy: 'プライバシーポリシー',
  },

  // チャット/会話
  chat: {
    conversations: '会話',
    noConversations: '会話履歴がありません',
    loadingConversations: '会話を読み込み中...',
    conversationHistory: '会話履歴',
    backToList: 'リストに戻る',
    totalTurns: '合計 {count} ターン',
    loadedTurns: '{count} 件読み込み済み',
    noContent: 'この会話にはコンテンツがありません',
    loadingConversation: '会話を読み込み中...',
    sessionTitle: 'セッションタイトル',
    createdAt: '作成日時',
    loadMore: 'さらに読み込む',
    remainingTurns: '残り {count} 件',
    allLoaded: 'すべて読み込み済み',
    allLoadedMessage: 'すべての {count} 件の会話を読み込みました',
    browseHistory: '会話履歴を閲覧・表示',
    totalSessions: '合計 {count} セッション',
    startNewConversation: '新しい会話を始めましょう',
    deleteSession: 'セッション削除',
    confirmDeleteSession: 'セッション削除の確認',
    deleteSessionWarning: 'セッション "{title}" を削除してもよろしいですか？\nこの操作は元に戻せません。セッションとすべての会話記録が永久に削除されます。',
    confirmDelete: '削除確認',
    turnsCount: '{count} ターン',
    preview: 'プレビュー {count} 件',
  },

  // 購入/支払い
  purchase: {
    title: '購入',
    buyTokens: 'トークン購入',
    orderHistory: '注文履歴',
    noOrders: '注文履歴がありません',
    loadingOrders: '注文を読み込み中...',
    paymentMethod: '支払い方法',
    amount: '金額',
    status: 'ステータス',
    date: '日付',
    processing: '処理中',
    completed: '完了',
    failed: '失敗',

    // 購入ページ
    subscriptionPlans: 'サブスクリプションプラン',
    subscriptionDescription: '月額サブスクリプションを選択して、すべての機能を無制限に利用し、プレミアムサポートを受けましょう',
    subscribeNow: '今すぐ購読',
    tokenPacks: 'トークンパック',
    tokenPacksDescription: '月額コミットメントなしで従量課金制のトークンパックを購入',
    buyNow: '今すぐ購入',
    oneTimePayment: '一回払い',
    popular: '人気',
    recommended: 'おすすめ',
    perMonth: '/月',
    tokens: 'トークン',
    features: '機能：',

    // トークンパック機能
    tokensIncluded: 'トークン含む',
    noExpirationDate: '有効期限なし',
    payOnceUseAnytime: '一度支払えばいつでも使用可能',
    perfectForOccasionalUsers: '時々利用するユーザーに最適',

    // 支払い方法選択
    selectPaymentMethod: '支払い方法を選択',
    selectPaymentDescription: 'お好みの支払い方法を選択して、安全に購入を完了してください。',
    alipayName: 'Alipay (支付宝)',
    alipayDescription: 'AlipayのQRコードによる安全な支払い - 高速で信頼性が高い',
    polarName: 'Polar.sh',
    polarDescription: '国際決済プラットフォーム - グローバルアクセス可能',
    creatingOrder: '注文作成中...',
    cancel: 'キャンセル',

    // 支払い処理
    scanToPay: 'スキャンして支払い',
    completePayment: '支払い完了',
    alipayPaymentDescription: 'AlipayアプリでQRコードをスキャンして安全に支払いを完了してください。',
    polarPaymentDescription: '開いたウィンドウで支払いを完了してください。支払い完了を自動的に検出します。',
    paymentWindowOpened: '支払いウィンドウが開きました',
    completePaymentInNewWindow: '新しいウィンドウで支払いを完了',
    openInBrowser: 'ブラウザで開く',
    reopenPaymentWindow: '支払いウィンドウを再度開く',
    checkingStatus: 'ステータス確認中...',
    checkPaymentStatus: '支払いステータスを確認',
    close: '閉じる',
    orderId: '注文ID',

    // 支払いステータス
    waitingForPayment: '支払い待ち中...',
    paymentReceived: '支払いを受信、処理中...',
    paymentSuccessful: '支払い成功！',
    paymentFailed: '支払いに失敗しました。もう一度お試しください。',
    paymentExpired: '支払いが期限切れです。新しい注文を作成してください。',
  },

  // 管理者
  admin: {
    title: '管理パネル',
  },

  // デバッグ/開発
  debug: {
    debugPanel: 'デバッグパネル',
    testEvents: 'テストイベント',
    parseEvents: 'イベント解析',
    clearEvents: 'テストイベントをクリア',
    clearAll: 'すべてクリア',
    eventData: 'イベントJSONデータ',
    parseError: '解析エラー',
    testAndParse: 'テストして解析',
    inputPlaceholder: 'SessionEvent[] JSONデータを入力...',
  },

  // UIコンポーネント
  ui: {
    swap: '交換',
    siderbarTitle: '主要機能',
    selectLanguageDescription: 'アプリケーションのインターフェース言語を選択',
    translationLanguageSettings: '翻訳言語設定',
    comingSoon: '機能は近日公開予定...',
    audioSettingsDescription: 'マイクとスピーカーの設定を構成',
    accountSettingsDescription: 'ユーザーアカウントとプロフィールを管理',
    microphoneAccessDenied: 'マイクアクセスが拒否されました',
    microphoneInstructions: 'ブラウザ設定でマイクアクセスを許可してから、ページを更新してください',
    refreshPage: 'ページを更新',
    openSettings: '設定',
    dismiss: '閉じる',
    microphonePermissionSteps: '以下の手順でマイク権限を有効にしてください：',
    chromeInstructions: 'アドレスバーの左側にある鍵アイコンをクリック → 「サイトの設定」を選択 → マイクアクセスを許可',
    firefoxInstructions: 'アドレスバーの左側にある盾アイコンをクリック → 「権限」を選択 → マイクアクセスを許可',
    safariInstructions: 'Safariメニューをクリック → 環境設定 → Webサイト → マイク → このWebサイトのアクセスを許可',
    genericInstructions: 'ブラウザ設定でこのWebサイトのマイクアクセスを許可してください',
    settingsCompleteRefresh: '設定完了後、ページを更新してください。',
    iosPermissionTitle: 'iOS マイク権限設定',
    iosPermissionDescription: 'iOSでは訪問のたびにマイク権限の再認証が必要です。以下の手順に従ってください：',
    iosPermissionTip: 'これはiOSのセキュリティ機能で、新しいセッションごとに再認証が必要です。より良い体験のために、このWebサイトをホーム画面にWebアプリとして追加することをお勧めします。',
    iosHelp: 'iOS ヘルプ',
    retryPermission: '再試行',
    openSystemSettings: '設定を開く',
  },

  // 会話
  conversation: {
    noContent: '会話コンテンツがありません',
    startSpeaking: '話し始めてください、自動翻訳',
    autoTranslate: '自動翻訳',
    turnsCount: '{count} 件の会話',
    tokens: 'トークン',
    totalCost: '総費用',
    translation: '翻訳',
    userA: 'ユーザーA',
    userB: 'ユーザーB',
    cancelled: 'キャンセル済み',
    translationIncomplete: '翻訳未完了',
  },
};
